import { TabConfig, Locales } from "../../types/tabs.ts";
import type {
  MagnoliaSpaConfig,
  SearchSpaConfig,
} from "../../types/search-spa-config.ts";
import { DateTime } from "luxon";
import { getEnvVar } from "./env";
import { USER_GROUP } from "vacv-components";

const toursB2B_EN = getEnvVar("VITE_B2B_TOURS_SEARCH_PUNCHOUT_URL_EN");
const toursB2B_FR = getEnvVar("VITE_B2B_TOURS_SEARCH_PUNCHOUT_URL_FR");

export const getLinkHref = (
  tab: TabConfig,
  audience: USER_GROUP,
  language: Locales,
  sirevsid?: string | null
): string => {
  if (tab?.audience?.[audience]?.linkTo?.[language]) {
    if (tab.id === "tours_tab" && sirevsid) {
      // tours tab for b2b (travel agents) gets overridden by the environment variable, with the sirevsid (sid) value appended
      const url = language === "fr" ? toursB2B_FR : toursB2B_EN;
      return url + "?sid=" + sirevsid;
    }

    return tab?.audience?.[audience]?.linkTo?.[language];
  } else if (tab.linkTo && tab.linkTo[language]) {
    return tab.linkTo[language];
  } else {
    // no link is invalid
    return "";
  }
};

export const getLinkTarget = (
  tab: TabConfig,
  audience: USER_GROUP
): string | undefined => {
  const newTab =
    tab.audience?.[audience]?.linkTo?.newTab ?? !!tab.linkTo?.newTab;
  return newTab ? "_blank" : undefined;
};

export const isValidTab = (
  tab: TabConfig,
  audience: USER_GROUP,
  language: Locales
): boolean => {
  if (audience && tab.audience) {
    // filter out the tab if the display property is false for this audience
    const tabAudiences = tab.audience;
    if (
      typeof tabAudiences != "undefined" &&
      audience in tabAudiences &&
      tabAudiences[audience].display
    ) {
      if (tabAudiences[audience].display === "false") {
        return false;
      }
    }
  }

  if (tab.searchType === "link") {
    // filter out invalid links
    const link = getLinkHref(tab, audience, language);
    if (!link) {
      return false;
    }
  }
  return true;
};

export function getDefaultTabId(
  configDefaultTabId: string,
  filteredTabs: TabConfig[]
): string {
  // For descripton of default tab selection see 3.6 of tech spec https://aircanadavacations.atlassian.net/browse/HD-967

  if (configDefaultTabId) {
    // After the tab control is generated with configurations, the tab with the “id” indicated by “defaultTabId“ becomes selected by default,
    for (const tab of filteredTabs) {
      if (tab.id === configDefaultTabId) {
        return configDefaultTabId;
      }
    }
  }

  const allLinks = filteredTabs.every((tab) => tab.searchType === "link");

  if (!allLinks) {
    // If at least one tab NOT act as link (i.e “searchType” is NOT “link”)
    const nonLinkTabs = filteredTabs.find((tab) => tab.searchType !== "link");
    if (nonLinkTabs) {
      // The  tab of the first non-link tab becomes selected
      return nonLinkTabs.id;
    }
  }

  return filteredTabs[0].id;
}

export const getLabel = (
  tab: TabConfig,
  audience: USER_GROUP,
  language: Locales
) => {
  // uncovered
  return tab.audience?.[audience]?.label?.[language] ?? tab.label[language];
};

const yyyy_mm_dd = "yyyy-MM-dd";

export function getEarliestBookableDate(): string {
  return DateTime.now()
    .set({
      hour: 0,
      minute: 0,
      second: 0,
    })
    .plus({
      days: 2,
    })
    .toFormat(yyyy_mm_dd);
}

export function getLatestBookableDate(): string {
  const earliestBookableDate = DateTime.now()
    .set({
      hour: 0,
      minute: 0,
      second: 0,
    })
    .plus({
      days: 2,
    });

  const lastVisibleMonth = earliestBookableDate
    .set({
      day: 1,
    })
    .plus({
      year: 1,
    })
    .minus({
      month: 1,
    });

  const lastBookableDate = DateTime.fromObject({
    year: lastVisibleMonth.year,
    month: lastVisibleMonth.month,
    day: lastVisibleMonth.daysInMonth ?? 0,
  });

  return lastBookableDate.toFormat(yyyy_mm_dd);
}

export function processSearchSpaConfig(
  magnoliaConfig: MagnoliaSpaConfig,
  audience: USER_GROUP,
  language: Locales,
  sirevsid?: string | null
): SearchSpaConfig {
  // SearchSpaConfig is the same as MagnoliaSpaConfig, but with these generated values:
  const filteredTabs = getFilteredTabs(
    magnoliaConfig,
    audience,
    language,
    sirevsid ?? null
  );
  const defaultTabId = getDefaultTabId(
    magnoliaConfig.defaultTabId,
    filteredTabs
  );

  const searchSpaConfig = {
    ...magnoliaConfig,
    audience,
    filteredTabs,
    defaultTabId,
  };
  return searchSpaConfig;
}

export function getSearchSpaConfig(
  audience: USER_GROUP,
  language: Locales,
  sirevsid: string | null
): SearchSpaConfig | undefined {
  // uncovered
  const spaContainer = document.getElementById("vacv-spa-search-container");
  if (spaContainer?.dataset?.config) {
    const config = JSON.parse(
      spaContainer?.dataset?.config
    ) as MagnoliaSpaConfig;

    return processSearchSpaConfig(config, audience, language, sirevsid);
  }
}

export function getFilteredTabs(
  magnoliaConfig: MagnoliaSpaConfig,
  audience: USER_GROUP,
  language: Locales,
  sirevsid: string | null
): TabConfig[] {
  const filteredTabs: TabConfig[] = [];

  for (const tab of magnoliaConfig.tabs) {
    const isValid = isValidTab(tab, audience, language);
    if (isValid) {
      const linkLabel = getLabel(tab, audience, language);

      let linkHref, linkTarget;
      if (tab.searchType === "link") {
        linkHref = getLinkHref(tab, audience, language, sirevsid);
        linkTarget = getLinkTarget(tab, audience);
      }

      const minDate = getEarliestBookableDate();
      const maxDate = getLatestBookableDate();

      filteredTabs.push({
        ...tab,
        linkLabel,
        linkHref,
        linkTarget,
        minDate,
        maxDate,
        isModifySearch: tab.forModifySearch === "true",
      });
    }
  }
  return filteredTabs;
}
